import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Grid, Drawer, Backdrop, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Divider, Button } from '@mui/material';
import { AutoAwesome, Info, InfoOutlined } from '@mui/icons-material';
import { PrimaryButton, SecondaryButton } from '../components';
import { RadioForm } from '../components/RadioForm';
import InfoHeader from '../components/InfoHeader';
import ReactPlayer from 'react-player';
import Empresa from '../types/negocio/Empresa';
import { useAuth } from '../context/AuthContext';
import 'react-toastify/dist/ReactToastify.css';
import toastService from '../utils/toastService';
import LoadingIndicator from '../components/LoadingIndicator';
import BasicModal from '../components/modais/BasicModal';
import { empresaService } from '../services/EmpresaService';

const Fundamentos: React.FC = () => {
  const [empresa, setEmpresa] = useState<Empresa>({} as Empresa);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [currentExamples, setCurrentExamples] = useState<{ title: string; content: React.ReactNode }>();
  const [completedSteps, setCompletedSteps] = useState<boolean[]>([false, false, false, false]);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const setStepCompleted = (step: number, isCompleted: boolean) => {
    setCompletedSteps(prev => {
      const newSteps = [...prev];
      newSteps[step - 1] = isCompleted;
      return newSteps;
    });
  };

  // Load empresa data when component mounts
  useEffect(() => {
    const fetchEmpresa = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await empresaService.getEmpresa();
        if (data) {
          setEmpresa(data);
        }
      } catch (err) {
        console.error("Erro ao carregar empresa:", err);
        setError("Não foi possível carregar os dados da empresa. Por favor, tente novamente.");
        toastService.error("Erro ao carregar dados da empresa");
      } finally {
        setIsLoading(false);
      }
    };

    if (user?.guid) {
      fetchEmpresa();
    }
  }, [user?.guid]);

  useEffect(() => {
    const isInitialsComplete =
      empresa.nome?.trim() &&
      empresa.anoInicioPlanejamento &&
      empresa.anosPlanejamento;

    setStepCompleted(1, !!isInitialsComplete);
  }, [empresa.nome, empresa.anoInicioPlanejamento, empresa.anosPlanejamento]);

  useEffect(() => {
    setStepCompleted(2, !!empresa.missao?.trim());
  }, [empresa.missao]);

  useEffect(() => {
    setStepCompleted(3, !!empresa.visao?.trim());
  }, [empresa.visao]);

  useEffect(() => {
    setStepCompleted(4, !!empresa.valores?.trim());
  }, [empresa.valores]);

  const handleOpenInfo = (section: 'missao' | 'visao' | 'valores') => {
    const examples = {
      missao: (
        <>
          <strong>Google</strong>: “Organizar as informações do mundo todo e torná-las acessíveis e úteis em caráter universal”.<br />
          <strong>Microsoft</strong>: “Ajudar as pessoas e empresas em todo o mundo a concretizarem todo o seu potencial”.<br />
          <strong>Vale</strong>: "Transformar recursos naturais em prosperidade e desenvolvimento sustentável."
        </>
      ),
      visao: (
        <>
          <strong>Petrobras</strong>: “Ser reconhecida como a melhor empresa de energia, garantindo o máximo valor para os seus acionistas e para a sociedade”.<br />
          <strong>Magazine Luiza</strong>: “Ser a maior e melhor plataforma digital de varejo do Brasil, conectando pessoas e negócios”.<br />
          <strong>Amazon</strong>: "Ser a empresa mais centrada no cliente do mundo, onde as pessoas podem encontrar e descobrir qualquer coisa que queiram comprar."
        </>
      ),
      valores: (
        <>
          <strong>Natura</strong>: “Bem-estar. Transparência nas relações. Compromisso com a verdade. Sustentabilidade como princípio”.<br />
          <strong>Apple</strong>: “Inovação contínua. Excelência em design e simplicidade. Foco na experiência do cliente".<br />
          <strong>Tesla</strong>: "Sustentabilidade ambiental. Inovação disruptiva. Ética e excelência no trabalho".
        </>
      )
    };

    setCurrentExamples({
      title: `Exemplos de ${section.charAt(0).toUpperCase() + section.slice(1)}`,
      content: examples[section]
    });
    setIsInfoModalOpen(true);
  };

  const handleChange = (field: keyof Empresa, value: any) => {
    setEmpresa((prev: Empresa) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      //setIsLoading(true);
      await empresaService.save(empresa);
      toastService.success("Fundamentos salvos com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar empresa:", error);
      setError("Erro ao salvar os dados da empresa. Por favor, tente novamente.");
      toastService.error("Erro ao salvar os fundamentos. Por favor, tente novamente.");
    } finally {
      // setIsLoading(false);
    }
  };

  const toggleSidebar = (open: boolean) => {
    setIsSidebarOpen(open);
  };

  // Show loading state when data is being fetched
  if (isLoading) {
    return <LoadingIndicator fullHeight message="Carregando dados da empresa..." />;
  }

  // Show error state only after loading is complete and there's an error
  if (error && !isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
        <Typography color="error">{error}</Typography>
        <PrimaryButton onClick={() => window.location.reload()}>
          Tentar Novamente
        </PrimaryButton>
      </Box>
    );
  }

  return (
    <>
      <InfoHeader
        title='Fundamentos'
        message='Discuta e descida os fundamentos que serão base para o planejamento estratégico da sua empresa.'
        isImportant
        url='https://youtu.be/q8orQeP9zAQ'
        modalTitle='Entenda a aba Diretrizes'
      />
      <Box sx={{ padding: 4, maxWidth: 800, margin: 'auto' }}>
        <Box sx={{
          background: (theme) => theme.palette.primary.main,
          width: '100%',
          height: 200,
          borderRadius: 2,
          marginBottom: 6
        }} >
          <Box sx={{ display: 'flex', padding: 4, gap: 1 }}>
            <img src="/logo512teste.png" alt="Logo" style={{ width: 44, height: 32, objectFit: 'cover' }} />
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'semibold' }}>
              Fundamentos da Empresa
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            px: 4,
            mt: 1,
            '& hr': {
              flexGrow: 1,
              height: '1.5px',
              border: 'none',
              mt: 2
            }
          }}>
            <RadioForm label="Iniciais" isCompleted={completedSteps[0]} step={1} />
            <Divider style={{ maxWidth: '100px', backgroundColor: completedSteps[0] ? 'green' : 'white' }} />
            <RadioForm label="Missão" isCompleted={completedSteps[1]} step={2} />
            <Divider style={{ maxWidth: '100px', backgroundColor: completedSteps[1] ? 'green' : 'white' }} />
            <RadioForm label="Visão" isCompleted={completedSteps[2]} step={3} />
            <Divider style={{ maxWidth: '100px', backgroundColor: completedSteps[2] ? 'green' : 'white' }} />
            <RadioForm label="Valores" isCompleted={completedSteps[3]} step={4} />
          </Box>
        </Box>
        <Backdrop
          open={isSidebarOpen}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer - 1,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          }}
        />
        <Typography variant="h6" sx={{ mb: 3 }} gutterBottom>
          Informações Iniciais para o Planejamento
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Nome da Empresa"
              variant="outlined"
              color="secondary"
              value={empresa.nome}
              onChange={(e) => handleChange('nome', e.target.value)}
            />
          </Grid>

          {/* Campos em linha abaixo - Lado a lado */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Ano de Início do Planejamento"
              variant="outlined"
              color="secondary"
              type="number"
              value={empresa.anoInicioPlanejamento}
              onChange={(e) => handleChange('anoInicioPlanejamento', Number(e.target.value))}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Quantidade em Anos de Planejamento"
              variant="outlined"
              color="secondary"
              type="number"
              value={empresa.anosPlanejamento}
              onChange={(e) => handleChange('anosPlanejamento', Number(e.target.value))}
            />
          </Grid>
        </Grid>

        {/* Modal com o video de ajuda */}
        <BasicModal
          title="Missão, Visão e Valores"
          open={isVideoModalOpen}
          onClose={() => setIsVideoModalOpen(false)}
          sizeContent='medium'
        >
          <Box sx={{ position: 'relative', paddingTop: '56.2%' }}>
            <ReactPlayer
              url='https://www.youtube.com/watch?v=skg-29jvfsU'
              width='100%'
              height='100%'
              style={{ position: 'absolute', top: 0, left: 0 }}
              controls
            />
          </Box>
        </BasicModal>

        <Box mt={4}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ m: 0 }} gutterBottom>
              Fundamentos
            </Typography>
            <IconButton
              onClick={() => setIsVideoModalOpen(true)}
              size="medium"
              sx={{ color: "black", cursor: 'pointer' }}
            >
              <InfoOutlined fontSize='small' />
            </IconButton>
            <Box sx={{ width: '500px' }}></Box>
            <PrimaryButton
              variant="contained"
              color="primary"
              onClick={() => toggleSidebar(true)}
              sx={{ height: 57 }}
            >
              <AutoAwesome fontSize='small' />
            </PrimaryButton>
          </Box>
          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              variant="outlined"
              label="Missão"
              color='secondary'
              value={empresa.missao}
              onChange={(e) => handleChange('missao', e.target.value)}
            />
            <PrimaryButton
              variant="contained"
              color="primary"
              onClick={() => handleOpenInfo('missao')}
              sx={{ height: 57 }}
            >
              <Info fontSize='medium' />
            </PrimaryButton>
          </Box>
        </Box>
        <Box mt={4}>
          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              variant="outlined"
              label="Visão"
              color='secondary'
              value={empresa.visao}
              onChange={(e) => handleChange('visao', e.target.value)}
            />
            <PrimaryButton
              onClick={() => handleOpenInfo('visao')}
              sx={{ height: 57 }}
            >
              <Info fontSize='medium' />
            </PrimaryButton>
          </Box>
        </Box>
        <Box mt={4}>
          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              variant="outlined"
              label="Valores"
              color='secondary'
              value={empresa.valores}
              onChange={(e) => handleChange('valores', e.target.value)}
            />
            <PrimaryButton
              onClick={() => handleOpenInfo('valores')}
              sx={{ height: 57 }}
            >
              <Info fontSize='medium' />
            </PrimaryButton>
          </Box>
        </Box>

        <Box mt={4} display="flex" justifyContent="center">
          <PrimaryButton onClick={handleSave}>
            Salvar Fundamentos
          </PrimaryButton>
        </Box>


        {/* Sidebar (Criar com IA) */}
        <Drawer
          anchor="right"
          open={isSidebarOpen}
          onClose={() => toggleSidebar(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: '33%',
              padding: 4,
            },
          }}
        >
          <Typography variant="h5">Construindo sua Missão!</Typography>
          <Typography variant="body2" sx={{ marginBottom: 1, marginTop: 4 }}>
            Qual é o produto ou serviço que oferecemos?
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            color='secondary'
            value={empresa.missao}
            onChange={(e) => handleChange('missao', e.target.value)}
            sx={{ marginBottom: 2 }}
            InputProps={{
              sx: {
                height: 48
              },
            }}
          />
          <Typography variant="body2" sx={{ marginBottom: 1, marginTop: 1 }}>
            Qual é a principal atividade da nossa empresa?
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            color='secondary'
            value={empresa.missao}
            onChange={(e) => handleChange('missao', e.target.value)}
            sx={{ marginBottom: 2 }}
            InputProps={{
              sx: {
                height: 48
              },
            }}
          />
          <Typography variant="body2" sx={{ marginBottom: 1, marginTop: 1 }}>
            Quais são os nossos métodos, diferenciais ou valores que guiam nossas operações?
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            color='secondary'
            value={empresa.missao}
            onChange={(e) => handleChange('missao', e.target.value)}
            sx={{ marginBottom: 2 }}
            InputProps={{
              sx: {
                height: 48
              },
            }}
          />
          <Typography variant="body2" sx={{ marginBottom: 1, marginTop: 1 }}>
            Que princípios, tecnologias ou abordagens utilizamos?
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            color='secondary'
            value={empresa.missao}
            onChange={(e) => handleChange('missao', e.target.value)}
            sx={{ marginBottom: 2 }}
            InputProps={{
              sx: {
                height: 48
              },
            }}
          />
          <Typography variant="body2" sx={{ marginBottom: 1, marginTop: 1 }}>
            Quem é nosso público-alvo ou cliente principal?
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            color='secondary'
            value={empresa.missao}
            onChange={(e) => handleChange('missao', e.target.value)}
            sx={{ marginBottom: 2 }}
            InputProps={{
              sx: {
                height: 48
              },
            }}
          />
          <Typography variant="body2" sx={{ marginBottom: 1, marginTop: 1 }}>
            Quais necessidades ou problemas resolvemos para esse público?
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            color='secondary'
            value={empresa.missao}
            onChange={(e) => handleChange('missao', e.target.value)}
            sx={{ marginBottom: 2 }}
            InputProps={{
              sx: {
                height: 48
              },
            }}
          />
          <Typography variant="body2" sx={{ marginBottom: 1, marginTop: 1 }}>
            Que contribuição queremos trazer para a sociedade, o mercado ou nossos clientes?
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            color='secondary'
            value={empresa.missao}
            onChange={(e) => handleChange('missao', e.target.value)}
            sx={{ marginBottom: 2 }}
            InputProps={{
              sx: {
                height: 48
              },
            }}
          />
          <Typography variant="body2" sx={{ marginBottom: 1, marginTop: 1 }}>
            Como nosso trabalho faz a diferença no mundo ou no cotidiano das pessoas?
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            color='secondary'
            value={empresa.missao}
            onChange={(e) => handleChange('missao', e.target.value)}
            sx={{ marginBottom: 2 }}
            InputProps={{
              sx: {
                height: 48
              },
            }}
          />
          <Typography variant="body2" sx={{ marginBottom: 1, marginTop: 1 }}>
            O que nos diferencia dos concorrentes?
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            color='secondary'
            value={empresa.missao}
            onChange={(e) => handleChange('missao', e.target.value)}
            sx={{ marginBottom: 2 }}
            InputProps={{
              sx: {
                height: 48
              },
            }}
          />
          <Typography variant="body2" sx={{ marginBottom: 1, marginTop: 1 }}>
            Por que os clientes escolhem a nossa empresa em vez de outra?
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            color='secondary'
            value={empresa.missao}
            onChange={(e) => handleChange('missao', e.target.value)}
            sx={{ marginBottom: 2 }}
            InputProps={{
              sx: {
                height: 48
              },
            }}
          />
          <Typography variant="body2" sx={{ marginBottom: 1, marginTop: 1 }}>
            Que princípios ou crenças fundamentais guiam nossas decisões e ações?
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            color='secondary'
            value={empresa.missao}
            onChange={(e) => handleChange('missao', e.target.value)}
            sx={{ marginBottom: 2 }}
            InputProps={{
              sx: {
                height: 48
              },
            }}
          />
          <Typography variant="body2" sx={{ marginBottom: 1, marginTop: 1 }}>
            Como demonstramos nossos valores no dia a dia?
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            color='secondary'
            value={empresa.missao}
            onChange={(e) => handleChange('missao', e.target.value)}
            sx={{ marginBottom: 2 }}
            InputProps={{
              sx: {
                height: 48
              },
            }}
          />
          <Box display={"flex"} justifyContent={"end"} gap={2}>
            <SecondaryButton
              variant="contained"
              onClick={() => toggleSidebar(false)}
              sx={{ marginTop: 2 }}
            >
              Fechar
            </SecondaryButton>
            <PrimaryButton
              onClick={() => toggleSidebar(false)}
              sx={{ marginTop: 2 }}
            >
              Gerar
            </PrimaryButton>
          </Box>
        </Drawer>
        <Dialog
          open={isInfoModalOpen}
          onClose={() => setIsInfoModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{currentExamples?.title}</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
              {currentExamples?.content}
            </Typography>
          </DialogContent>
          <DialogActions>
            <SecondaryButton variant='contained' onClick={() => setIsInfoModalOpen(false)}>Fechar</SecondaryButton>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default Fundamentos;